import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ref, update, remove, push } from "firebase/database";
import { useObjectVal } from "react-firebase-hooks/database";
import {
  Table,
  Tabs,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Popconfirm,
  message,
  Spin,
  Tag,
  Switch,
  Space,
  Typography,
} from "antd";
import {
  PlusCircle,
  PencilSimple,
  Trash,
  SignOut,
  ArrowLeft,
} from "@phosphor-icons/react";
import styled from "styled-components";
import { useAuth } from "../contexts/auth";
import { FirebaseService } from "../utility";

const { TextArea } = Input;
const { Title } = Typography;

const ADMIN_EMAILS = [
  "george@techmaven.us",
  "thedubaicashflowclub@gmail.com",
];

// --- Styled Components ---

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #1a2332;
  padding: 24px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav::before {
    border-bottom-color: #2c3e50;
  }
  .ant-tabs-tab {
    color: #a1a1aa !important;
    font-size: 15px;
    &:hover {
      color: #fff !important;
    }
  }
  .ant-tabs-tab-active .ant-tabs-tab-btn {
    color: #fff !important;
  }
  .ant-tabs-ink-bar {
    background: #78c8b8;
  }
`;

const StyledTable = styled(Table)`
  .ant-table {
    background: #223043;
    border-radius: 8px;
    color: #e4e4e7;
  }
  .ant-table-thead > tr > th {
    background: #1a2332 !important;
    color: #a1a1aa !important;
    border-bottom: 1px solid #2c3e50;
    font-weight: 600;
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .ant-table-tbody > tr > td {
    border-bottom: 1px solid #2c3e50;
    color: #e4e4e7;
  }
  .ant-table-tbody > tr:hover > td {
    background: #2c3e50 !important;
  }
  .ant-table-tbody > tr.ant-table-row:hover > td {
    background: #2c3e50 !important;
  }
  .ant-pagination {
    .ant-pagination-item {
      background: #223043;
      border-color: #2c3e50;
      a {
        color: #e4e4e7;
      }
      &-active {
        border-color: #78c8b8;
        a {
          color: #78c8b8;
        }
      }
    }
    .ant-pagination-prev,
    .ant-pagination-next {
      .ant-pagination-item-link {
        background: #223043;
        border-color: #2c3e50;
        color: #e4e4e7;
      }
    }
  }
  .ant-empty-description {
    color: #a1a1aa;
  }
`;

const ActionButton = styled(Button)`
  &.ant-btn-text {
    color: #a1a1aa;
    &:hover {
      color: #fff;
      background: rgba(255, 255, 255, 0.08);
    }
  }
`;

const currency = (v) =>
  v != null ? `$${Number(v).toLocaleString()}` : "";

// --- Field definitions per card category ---

const bigDealFields = [
  { name: "name", label: "Name", required: true },
  { name: "type", label: "Type", required: true, input: "select", options: ["Real Estate", "Limited Partnership", "Property Damage", "Automated Business"] },
  { name: "description", label: "Description", input: "textarea" },
  { name: "rule", label: "Rule", input: "textarea", tooltip: "Instructions shown to the player, e.g. sell conditions, ROI info, or price range." },
  { name: "tag", label: "Tag", tooltip: "Short identifier shown on the player's financial statement (e.g. 8PLEX, DUPLEX, LP-Auto)." },
  { name: "cost", label: "Cost", input: "number", tooltip: "Total purchase price of the asset." },
  { name: "downPayment", label: "Down Payment", input: "number", tooltip: "Cash the player must pay upfront to acquire this deal." },
  { name: "mortgage", label: "Mortgage", input: "number", tooltip: "Loan amount (Cost minus Down Payment). Added to player's liabilities." },
  { name: "cashFlow", label: "Cash Flow", input: "number", tooltip: "Monthly income (or loss if negative) added to the player's passive income." },
  { name: "roi", label: "ROI", input: "number", step: 0.01, tooltip: "Return on investment as a decimal (e.g. 0.6 = 60%). Calculated as Cash Flow x 12 / Down Payment." },
  { name: "landType", label: "Land Type", tooltip: "Category used to match with Market Offer cards (e.g. plex, duplex, 3Br/2Ba, apartment, business)." },
  { name: "units", label: "Units", input: "number", tooltip: "Number of rental units. Used by plex/apartment offers that pay per unit." },
  { name: "liability", label: "Liability", input: "number", tooltip: "Direct liability amount (used for non-mortgage deals like automated businesses)." },
  { name: "propertyType", label: "Property Type (damage cards)", tooltip: "Which property type this damage card affects (e.g. 8-plex, rental). Only for Property Damage cards." },
];

const smallDealFields = [
  { name: "name", label: "Name", required: true },
  { name: "type", label: "Type", required: true, input: "select", options: ["Stock", "Mutual Fund", "Real Estate", "Certificate of Deposit", "Company", "Coin", "Preferred Stock"] },
  { name: "description", label: "Description", input: "textarea" },
  { name: "rule", label: "Rule", input: "textarea", tooltip: "Instructions shown to the player, e.g. who can buy/sell and at what price." },
  { name: "symbol", label: "Symbol", tooltip: "Ticker symbol for stocks/funds (e.g. MYT4U, GRO4US). Used to group same-stock cards together." },
  { name: "tag", label: "Tag", tooltip: "Short identifier shown on the player's financial statement." },
  { name: "price", label: "Price", input: "number", tooltip: "Price per share/unit for stocks, mutual funds, and coins." },
  { name: "cost", label: "Cost", input: "number", tooltip: "Total cost for real estate or company deals." },
  { name: "downPayment", label: "Down Payment", input: "number", tooltip: "Cash the player must pay upfront." },
  { name: "mortgage", label: "Mortgage", input: "number", tooltip: "Loan amount added to player's liabilities." },
  { name: "cashFlow", label: "Cash Flow", input: "number", tooltip: "Monthly passive income added to the player." },
  { name: "roi", label: "ROI", input: "number", step: 0.01, tooltip: "Return on investment as a decimal (e.g. 0.45 = 45%)." },
  { name: "dividend", label: "Dividend", input: "number", tooltip: "Per-share dividend paid to the player each time a dividend card is drawn for this stock." },
  { name: "range", label: "Price Range", tooltip: "Historic trading range shown to the player (e.g. '$1 to $30'). Helps them decide whether to buy." },
  { name: "landType", label: "Land Type", tooltip: "Category used to match with Market Offer cards (e.g. 2Br/1Ba, business)." },
  { name: "liability", label: "Liability", input: "number", tooltip: "Direct liability amount for non-mortgage deals." },
  { name: "businessType", label: "Business Type", tooltip: "Sub-type for Company cards (e.g. software, widget). Used to match with related offer cards." },
  { name: "stockSplit", label: "Stock Split", input: "switch", tooltip: "If enabled, this card doubles the player's shares of this stock instead of offering a buy/sell." },
  { name: "reverseSplit", label: "Reverse Split", input: "switch", tooltip: "If enabled, this card halves the player's shares of this stock." },
  { name: "splitRatio", label: "Split Ratio", tooltip: "The split multiplier (e.g. '2:1' for stock split, '1:2' for reverse split)." },
];

const doodadFields = [
  { name: "name", label: "Name", required: true },
  { name: "text", label: "Text", input: "textarea", required: true, tooltip: "Message displayed to the player when they draw this card." },
  { name: "cost", label: "Cost", input: "number", tooltip: "Fixed dollar amount deducted from the player's cash." },
  { name: "amount", label: "Amount (fraction of savings)", input: "number", step: 0.01, tooltip: "Takes a fraction of the player's savings instead of a fixed cost. E.g. 0.5 = 50% of savings. Leave empty if using fixed cost." },
  { name: "loan", label: "Loan", input: "number", tooltip: "Loan balance added to the player's liabilities (e.g. $17,000 boat loan)." },
  { name: "payment", label: "Monthly Payment", input: "number", tooltip: "Recurring monthly expense added to the player's financial statement alongside the loan." },
  { name: "key", label: "Liability Key", tooltip: "Links this doodad to a player liability. Only used for doodads that create ongoing monthly payments (e.g. boatLoan, creditCardPayment). Leave empty for one-time costs." },
  { name: "debt", label: "Debt Added", input: "number", tooltip: "Increases an existing liability balance (e.g. adds $4,000 to credit card debt for a Big Screen TV purchase)." },
  { name: "child", label: "Requires Children", input: "switch", tooltip: "If enabled, this card only applies to players who have children." },
  { name: "onlyOne", label: "Only One Allowed", input: "switch", tooltip: "If enabled, a player can only have one of this doodad (e.g. one boat loan)." },
];

const offerFields = [
  { name: "name", label: "Name", required: true },
  { name: "type", label: "Property Type", required: true, tooltip: "Must match the landType of deal cards this offer applies to (e.g. plex, apartment, 3Br/2Ba, limited, business, car wash, mall)." },
  { name: "description", label: "Description", input: "textarea" },
  { name: "rule1", label: "Rule 1", input: "textarea", tooltip: "Who can sell — shown to all players (e.g. 'Everyone may sell at this price')." },
  { name: "rule2", label: "Rule 2", input: "textarea", tooltip: "What happens when you sell — mortgage payoff, cash flow loss, etc." },
  { name: "offer", label: "Offer Price", input: "number", tooltip: "Fixed total price offered for the property. Used for houses, condos, and specific assets. Leave empty if using per-unit pricing." },
  { name: "offerPerUnit", label: "Offer Per Unit", input: "number", tooltip: "Price per unit for plex/apartment offers. Multiplied by the number of units the player owns. Leave empty if using fixed offer price." },
  { name: "cashFlow", label: "Cash Flow Change", input: "number", tooltip: "Change to monthly cash flow when this offer triggers (e.g. +400 for business improvement, -500 for brother-in-law deal)." },
  { name: "lowestUnit", label: "Min Units Required", input: "number", tooltip: "Minimum number of units the player's property must have to qualify (e.g. 12 for large apartment buyers). Set to 0 or leave empty for no minimum." },
];

// --- Column definitions per category ---

const bigDealColumns = [
  { title: "Key", dataIndex: "key", width: 180, ellipsis: true, sorter: (a, b) => a.key.localeCompare(b.key) },
  { title: "Name", dataIndex: "name", width: 200, ellipsis: true },
  { title: "Type", dataIndex: "type", width: 160, render: (v) => <Tag color="blue">{v}</Tag> },
  { title: "Cost", dataIndex: "cost", width: 120, render: currency, sorter: (a, b) => (a.cost || 0) - (b.cost || 0) },
  { title: "Down Payment", dataIndex: "downPayment", width: 130, render: currency },
  { title: "Cash Flow", dataIndex: "cashFlow", width: 110, render: (v) => v != null ? <span style={{ color: v >= 0 ? "#8CB369" : "#E15554" }}>{currency(v)}</span> : "" },
  { title: "ROI", dataIndex: "roi", width: 80, render: (v) => v != null ? `${(v * 100).toFixed(0)}%` : "" },
];

const smallDealColumns = [
  { title: "Key", dataIndex: "key", width: 180, ellipsis: true, sorter: (a, b) => a.key.localeCompare(b.key) },
  { title: "Name", dataIndex: "name", width: 200, ellipsis: true },
  { title: "Type", dataIndex: "type", width: 160, render: (v) => <Tag color="purple">{v}</Tag> },
  { title: "Symbol", dataIndex: "symbol", width: 100 },
  { title: "Price", dataIndex: "price", width: 100, render: currency },
  { title: "Cost", dataIndex: "cost", width: 100, render: currency },
  { title: "Cash Flow", dataIndex: "cashFlow", width: 110, render: (v) => v != null ? <span style={{ color: v >= 0 ? "#8CB369" : "#E15554" }}>{currency(v)}</span> : "" },
];

const doodadColumns = [
  { title: "Key", dataIndex: "key", width: 150, ellipsis: true, sorter: (a, b) => a.key.localeCompare(b.key) },
  { title: "Name", dataIndex: "name", width: 220, ellipsis: true },
  { title: "Cost", dataIndex: "cost", width: 100, render: currency, sorter: (a, b) => (a.cost || 0) - (b.cost || 0) },
  { title: "Amount", dataIndex: "amount", width: 100, render: (v) => v != null ? `${v * 100}%` : "" },
  { title: "Loan", dataIndex: "loan", width: 100, render: currency },
  { title: "Children", dataIndex: "child", width: 90, render: (v) => v ? <Tag color="cyan">Yes</Tag> : "" },
];

const offerColumns = [
  { title: "Key", dataIndex: "key", width: 150, ellipsis: true, sorter: (a, b) => a.key.localeCompare(b.key) },
  { title: "Name", dataIndex: "name", width: 220, ellipsis: true },
  { title: "Type", dataIndex: "type", width: 140, render: (v) => <Tag color="orange">{v}</Tag> },
  { title: "Offer", dataIndex: "offer", width: 130, render: currency },
  { title: "Per Unit", dataIndex: "offerPerUnit", width: 120, render: currency },
  { title: "Cash Flow", dataIndex: "cashFlow", width: 110, render: (v) => v != null ? <span style={{ color: v >= 0 ? "#8CB369" : "#E15554" }}>{currency(v)}</span> : "" },
];

// --- Card Editor Modal ---

function CardEditorModal({ open, onClose, onSave, card, fields, title }) {
  const [form] = Form.useForm();

  useEffect(() => {
    if (open) {
      form.resetFields();
      if (card) {
        form.setFieldsValue(card);
      }
    }
  }, [open, card, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      // Clean undefined/null values
      const cleaned = {};
      for (const [k, v] of Object.entries(values)) {
        if (v !== undefined && v !== null && v !== "") {
          cleaned[k] = v;
        }
      }
      onSave(cleaned);
    } catch {
      // validation failed
    }
  };

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onClose}
      onOk={handleSave}
      okText={card ? "Update" : "Create"}
      width={640}
      destroyOnClose
    >
      <Form form={form} layout="vertical" style={{ maxHeight: "60vh", overflowY: "auto", paddingRight: 8 }}>
        {fields.map((f) => {
          if (f.input === "textarea") {
            return (
              <Form.Item key={f.name} name={f.name} label={f.label} tooltip={f.tooltip} rules={f.required ? [{ required: true, message: `${f.label} is required` }] : undefined}>
                <TextArea rows={3} />
              </Form.Item>
            );
          }
          if (f.input === "number") {
            return (
              <Form.Item key={f.name} name={f.name} label={f.label} tooltip={f.tooltip} rules={f.required ? [{ required: true, message: `${f.label} is required` }] : undefined}>
                <InputNumber step={f.step || 1} style={{ width: "100%" }} />
              </Form.Item>
            );
          }
          if (f.input === "switch") {
            return (
              <Form.Item key={f.name} name={f.name} label={f.label} tooltip={f.tooltip} valuePropName="checked">
                <Switch />
              </Form.Item>
            );
          }
          if (f.input === "select") {
            return (
              <Form.Item key={f.name} name={f.name} label={f.label} tooltip={f.tooltip} rules={f.required ? [{ required: true, message: `${f.label} is required` }] : undefined}>
                <Select options={f.options.map((o) => ({ label: o, value: o }))} />
              </Form.Item>
            );
          }
          return (
            <Form.Item key={f.name} name={f.name} label={f.label} tooltip={f.tooltip} rules={f.required ? [{ required: true, message: `${f.label} is required` }] : undefined}>
              <Input />
            </Form.Item>
          );
        })}
      </Form>
    </Modal>
  );
}

// --- Card Table Section ---

function CardSection({ category, columns, fields, data }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();

  const dataSource = useMemo(() => {
    if (!data) return [];
    return Object.entries(data).map(([key, val]) => ({ key, ...val }));
  }, [data]);

  const handleAdd = () => {
    setEditingCard(null);
    setModalOpen(true);
  };

  const handleEdit = (record) => {
    setEditingCard(record);
    setModalOpen(true);
  };

  const handleDelete = async (record) => {
    try {
      await remove(ref(FirebaseService.database, `cards/${category}/${record.key}`));
      messageApi.success("Card deleted");
    } catch (err) {
      messageApi.error("Failed to delete: " + err.message);
    }
  };

  const handleSave = async (values) => {
    try {
      if (editingCard) {
        // Update existing
        await update(ref(FirebaseService.database, `cards/${category}/${editingCard.key}`), values);
        messageApi.success("Card updated");
      } else {
        // Create new — generate a key
        const newKey = window.prompt("Enter a unique key for this card (e.g., realEstateB36, doodad50, offer42):");
        if (!newKey || !newKey.trim()) return;
        const cleanKey = newKey.trim();
        if (data && data[cleanKey]) {
          messageApi.error("A card with this key already exists");
          return;
        }
        await update(ref(FirebaseService.database, `cards/${category}/${cleanKey}`), values);
        messageApi.success("Card created");
      }
      setModalOpen(false);
    } catch (err) {
      messageApi.error("Failed to save: " + err.message);
    }
  };

  const allColumns = [
    ...columns,
    {
      title: "Actions",
      width: 100,
      fixed: "right",
      render: (_, record) => (
        <Space>
          <ActionButton type="text" icon={<PencilSimple size={18} />} onClick={() => handleEdit(record)} />
          <Popconfirm title="Delete this card?" onConfirm={() => handleDelete(record)} okText="Delete" okButtonProps={{ danger: true }}>
            <ActionButton type="text" icon={<Trash size={18} />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ color: "#a1a1aa", fontSize: 14 }}>{dataSource.length} cards</span>
        <Button type="primary" icon={<PlusCircle size={18} />} onClick={handleAdd} style={{ background: "#78C8B8", borderColor: "#78C8B8", color: "#1a2332" }}>
          Add Card
        </Button>
      </div>
      <StyledTable
        columns={allColumns}
        dataSource={dataSource}
        pagination={{ pageSize: 15, showSizeChanger: true, pageSizeOptions: [10, 15, 25, 50] }}
        scroll={{ x: "max-content" }}
        size="middle"
      />
      <CardEditorModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        card={editingCard}
        fields={fields}
        title={editingCard ? `Edit: ${editingCard.name}` : "Add New Card"}
      />
    </>
  );
}

// --- Main Admin Screen ---

export function AdminScreen() {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [cardsData, loading] = useObjectVal(ref(FirebaseService.database, "cards"));

  // Admin gate
  if (!isAuthenticated || !user) {
    return (
      <PageWrapper style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <Title level={3} style={{ color: "#fff" }}>Please log in first</Title>
          <Button type="primary" onClick={() => navigate("/login")}>Go to Login</Button>
        </div>
      </PageWrapper>
    );
  }

  if (!ADMIN_EMAILS.includes(user.email)) {
    return (
      <PageWrapper style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <Title level={3} style={{ color: "#E15554" }}>Access Denied</Title>
          <p style={{ color: "#a1a1aa" }}>You are not authorized to access this page.</p>
          <Button onClick={() => navigate("/rooms")}>Back to Game</Button>
        </div>
      </PageWrapper>
    );
  }

  if (loading) {
    return (
      <PageWrapper style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Spin size="large" />
      </PageWrapper>
    );
  }

  const tabs = [
    {
      key: "bigDeal",
      label: `Big Deals (${cardsData?.bigDeal ? Object.keys(cardsData.bigDeal).length : 0})`,
      children: <CardSection category="bigDeal" columns={bigDealColumns} fields={bigDealFields} data={cardsData?.bigDeal} />,
    },
    {
      key: "smallDeal",
      label: `Small Deals (${cardsData?.smallDeal ? Object.keys(cardsData.smallDeal).length : 0})`,
      children: <CardSection category="smallDeal" columns={smallDealColumns} fields={smallDealFields} data={cardsData?.smallDeal} />,
    },
    {
      key: "doodad",
      label: `Doodads (${cardsData?.doodad ? Object.keys(cardsData.doodad).length : 0})`,
      children: <CardSection category="doodad" columns={doodadColumns} fields={doodadFields} data={cardsData?.doodad} />,
    },
    {
      key: "offer",
      label: `Market Offers (${cardsData?.offer ? Object.keys(cardsData.offer).length : 0})`,
      children: <CardSection category="offer" columns={offerColumns} fields={offerFields} data={cardsData?.offer} />,
    },
  ];

  return (
    <PageWrapper>
      <Header>
        <HeaderLeft>
          <Button
            type="text"
            icon={<ArrowLeft size={20} />}
            onClick={() => navigate("/rooms")}
            style={{ color: "#a1a1aa" }}
          />
          <Title level={3} style={{ color: "#fff", margin: 0 }}>
            Card Manager
          </Title>
        </HeaderLeft>
        <Space>
          <span style={{ color: "#a1a1aa", fontSize: 13 }}>{user.email}</span>
          <Button type="text" icon={<SignOut size={20} />} onClick={logout} style={{ color: "#a1a1aa" }} />
        </Space>
      </Header>
      <StyledTabs items={tabs} size="large" />
    </PageWrapper>
  );
}
